// src/pages/AlarmDetails/tabs/DeveloperTab.jsx
// Developer tab showing technical details, decision flow, and raw data

import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Tabs,
  Tab,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Code as CodeIcon,
  Router as AntennaIcon,
  DeviceHub as HubIcon,
  Memory as DeviceIcon,
  AccountTree as FlowIcon,
  ArrowForward as ArrowIcon,
  CheckCircle as CheckIcon,
  Cancel as CrossIcon,
  ContentCopy as CopyIcon,
  DataObject as JsonIcon
} from '@mui/icons-material';

// Flow step component with improved arrow placement
const FlowStep = ({ number, title, result, details, isLast = false }) => (
  <Box sx={{ position: 'relative' }}>
    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: isLast ? 0 : 1 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          backgroundColor: result === 'success' ? 'success.main' :
                          result === 'failed' ? 'error.main' :
                          result === 'skipped' ? 'grey.400' : 'primary.main',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 'bold',
          fontSize: '0.875rem',
          flexShrink: 0
        }}
      >
        {number}
      </Box>
      <Box sx={{ ml: 2, flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle2" fontWeight="bold">
            {title}
          </Typography>
          {result === 'success' && <CheckIcon color="success" fontSize="small" />}
          {result === 'failed' && <CrossIcon color="error" fontSize="small" />}
        </Box>
        {details && (
          <Typography
            variant="body2"
            color={result === 'failed' ? 'error.main' : 'text.secondary'}
            sx={{ wordBreak: 'break-word' }}
          >
            {details}
          </Typography>
        )}
      </Box>
    </Box>
    {/* Arrow pointing down to next step */}
    {!isLast && (
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', pl: '12px', mb: 1 }}>
        <ArrowIcon sx={{ color: 'grey.400', transform: 'rotate(90deg)', fontSize: '1.2rem' }} />
      </Box>
    )}
  </Box>
);

const DeveloperTab = ({ alarm }) => {
  const [subTab, setSubTab] = useState(0);

  // Build the decision flow
  const buildDecisionFlow = () => {
    const steps = [];
    const content = alarm?.Content || {};

    // Step 1: Button press received
    steps.push({
      number: 1,
      title: 'Button Press Received',
      result: 'success',
      details: `Device: ${content.deviceKey || 'Unknown'}, Channel: ${content.channel || 'N/A'}, Action: ${content.action || 'N/A'}`
    });

    // Step 2: Device lookup - use Content.buttonDeviceFound if available (new field)
    const deviceFound = content.buttonDeviceFound !== undefined
      ? content.buttonDeviceFound
      : !!alarm?.deviceModel;
    const deviceName = content.buttonDeviceName || alarm?.deviceModel?.friendlyName;
    const deviceId = content.buttonDeviceId || alarm?.deviceModel?.id;

    steps.push({
      number: 2,
      title: 'Device Lookup',
      result: deviceFound ? 'success' : 'failed',
      details: deviceFound
        ? `Found: ${deviceName || 'Unknown'} (ID: ${deviceId || 'N/A'})`
        : 'Device not found in database'
    });

    // Step 3: Location determination
    const locationName = alarm?.locationModel?.friendlyName || content.locationName;
    const locationId = alarm?.Location || alarm?.locationModel?.id;
    const locationSource = content.locationSource || 'signal-based';
    const locationConfidence = content.locationConfidence || 'medium';
    const hasLocation = locationId || locationName;
    const personLocationName = alarm?.personModel?.locationModel?.friendlyName || content.personLocationName;

    let locationDetails;
    if (hasLocation) {
      // Show the source and confidence
      let sourceLabel;
      switch (locationSource) {
        case 'person':
          sourceLabel = `Person's location "${personLocationName || locationName}"`;
          break;
        case 'database':
          sourceLabel = `Fixed button location "${locationName}"`;
          break;
        case 'antenna':
          sourceLabel = `Strongest antenna signal location "${locationName}"`;
          break;
        case 'hub':
          sourceLabel = `Hub-provided location "${locationName}"`;
          break;
        default:
          sourceLabel = `${locationSource} → "${locationName || 'Unknown'}"`;
      }
      locationDetails = `${sourceLabel} (ID: ${locationId || 'N/A'}, Confidence: ${locationConfidence.toUpperCase()})`;
    } else {
      // Build a clearer message for no location
      const strongestAntenna = content.antennas?.length > 0
        ? content.antennas.reduce((best, ant) => (!best || (ant.rssiDbm > best.rssiDbm)) ? ant : best, null)
        : null;
      if (strongestAntenna) {
        locationDetails = `Using strongest antenna signal: ${strongestAntenna.antennaName || 'Unknown'} (${strongestAntenna.rssiDbm} dBm, ${strongestAntenna.signalQuality || 'unknown quality'})`;
      } else {
        locationDetails = 'No antenna signals received - location cannot be determined';
      }
    }

    steps.push({
      number: 3,
      title: 'Location Determination',
      result: hasLocation ? 'success' : (content.antennas?.length > 0 ? 'warning' : 'failed'),
      details: locationDetails
    });

    // Step 4: Alarm type determination
    steps.push({
      number: 4,
      title: 'Alarm Type Determination',
      result: alarm?.alarmTypeModel ? 'success' : 'failed',
      details: alarm?.alarmTypeModel
        ? `Type: ${alarm.alarmTypeModel.friendlyName} (Countdown: ${alarm.alarmTypeModel.countdownSeconds}s)`
        : 'Could not determine alarm type'
    });

    // Step 5: Alarm creation
    steps.push({
      number: 5,
      title: 'Alarm Created',
      result: 'success',
      details: `Alarm ID: ${alarm?.id}, UUID: ${alarm?.alarm_UUID?.slice(0, 8)}...`
    });

    // Step 6: Countdown started
    steps.push({
      number: 6,
      title: 'Countdown Started',
      result: 'success',
      details: `Duration: ${alarm?.alarmTypeModel?.countdownSeconds || 30} seconds`
    });

    // Step 7: Notification sent (if applicable)
    if (alarm?.sentToEmergencyAt) {
      steps.push({
        number: 7,
        title: 'Emergency Notification Sent',
        result: 'success',
        details: `Sent at ${new Date(alarm.sentToEmergencyAt).toLocaleString()}, Count: ${alarm.notificationCount}`
      });
    }

    return steps;
  };

  const decisionFlow = buildDecisionFlow();

  // Copy JSON to clipboard
  const copyToClipboard = (data) => {
    navigator.clipboard.writeText(JSON.stringify(data, null, 2));
  };

  return (
    <Box>
      {/* Warning banner */}
      <Alert severity="info" sx={{ mb: 3 }}>
        This tab contains technical information for developers and debugging purposes.
      </Alert>

      {/* Sub-tabs - Hub Info removed per user request */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={subTab} onChange={(e, v) => setSubTab(v)}>
          <Tab icon={<FlowIcon />} iconPosition="start" label="Decision Flow" />
          <Tab icon={<DeviceIcon />} iconPosition="start" label="Device Details" />
          <Tab icon={<AntennaIcon />} iconPosition="start" label="Antenna Data" />
          <Tab icon={<JsonIcon />} iconPosition="start" label="Raw Data" />
        </Tabs>
      </Paper>

      {/* Decision Flow Tab */}
      {subTab === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FlowIcon color="primary" />
                  Alarm Processing Flow
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Box sx={{ pl: 1 }}>
                  {decisionFlow.map((step, index) => (
                    <FlowStep
                      key={step.number}
                      {...step}
                      isLast={index === decisionFlow.length - 1}
                    />
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Processing Summary
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <TableContainer>
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell component="th">Button Type</TableCell>
                        <TableCell>
                          <Chip
                            label={alarm?.Content?.buttonType || alarm?.deviceModel?.config?.buttonType || 'Unknown'}
                            size="small"
                            color={alarm?.Content?.buttonType ? 'primary' : 'default'}
                          />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell component="th">Device HEX</TableCell>
                        <TableCell>
                          <code>{alarm?.Content?.deviceKey || alarm?.deviceModel?.config?.identity?.HEX || 'N/A'}</code>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell component="th">Device Found</TableCell>
                        <TableCell>
                          {alarm?.Content?.buttonDeviceFound !== undefined ? (
                            <Chip
                              label={alarm.Content.buttonDeviceFound ? 'Yes' : 'No'}
                              size="small"
                              color={alarm.Content.buttonDeviceFound ? 'success' : 'error'}
                            />
                          ) : (
                            alarm?.deviceModel ? 'Yes' : 'No'
                          )}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell component="th">Device ID</TableCell>
                        <TableCell>{alarm?.Content?.buttonDeviceId || alarm?.deviceModel?.id || 'N/A'}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell component="th">Device Name</TableCell>
                        <TableCell>{alarm?.Content?.buttonDeviceName || alarm?.deviceModel?.friendlyName || 'N/A'}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell component="th">Location Source</TableCell>
                        <TableCell>
                          <Chip
                            label={alarm?.Content?.locationSource || alarm?.locationReasoning?.split(':')[0] || 'Unknown'}
                            size="small"
                            variant="outlined"
                            color={alarm?.Content?.locationSource === 'person' ? 'success' :
                                   alarm?.Content?.locationSource === 'database' ? 'primary' : 'default'}
                          />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell component="th">Location Confidence</TableCell>
                        <TableCell>
                          <Chip
                            label={alarm?.Content?.locationConfidence?.toUpperCase() || 'UNKNOWN'}
                            size="small"
                            color={alarm?.Content?.locationConfidence === 'high' ? 'success' :
                                   alarm?.Content?.locationConfidence === 'medium' ? 'warning' : 'default'}
                          />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell component="th">Location ID</TableCell>
                        <TableCell>{alarm?.Location || 'None'}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell component="th">Alarm Type ID</TableCell>
                        <TableCell>{alarm?.AlarmType || 'N/A'}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell component="th">Countdown Duration</TableCell>
                        <TableCell>{alarm?.alarmTypeModel?.countdownSeconds || 30}s</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell component="th">Antennas Received</TableCell>
                        <TableCell>{alarm?.Content?.antennas?.length || 0}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell component="th">Strongest Antenna</TableCell>
                        <TableCell>{alarm?.Content?.strongestAntennaName || 'N/A'}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell component="th">Access Token</TableCell>
                        <TableCell>
                          <code>{alarm?.accessToken?.slice(0, 16)}...</code>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Device Details Tab */}
      {subTab === 1 && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DeviceIcon color="primary" />
                Button Device Details
              </Typography>
              {(alarm?.deviceModel || alarm?.Content?.buttonDeviceFound) && (
                <Tooltip title="Copy device config">
                  <IconButton onClick={() => copyToClipboard(alarm.deviceModel || alarm.Content)}>
                    <CopyIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            <Divider sx={{ mb: 2 }} />

            {/* Show device info from deviceModel OR from Content (when Device FK is null due to exclusive constraint) */}
            {(alarm?.deviceModel || alarm?.Content?.buttonDeviceFound) ? (
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Basic Info</Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell>ID</TableCell>
                          <TableCell>{alarm?.deviceModel?.id || alarm?.Content?.buttonDeviceId || 'N/A'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>UUID</TableCell>
                          <TableCell><code>{alarm?.deviceModel?.device_UUID || 'N/A'}</code></TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Friendly Name</TableCell>
                          <TableCell>{alarm?.deviceModel?.friendlyName || alarm?.Content?.buttonDeviceName || 'N/A'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Device HEX Code</TableCell>
                          <TableCell><code>{alarm?.Content?.deviceKey || alarm?.deviceModel?.config?.identity?.HEX || 'N/A'}</code></TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Device Type</TableCell>
                          <TableCell>{alarm?.deviceModel?.deviceType || alarm?.Content?.deviceType || 'N/A'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Button Type</TableCell>
                          <TableCell>
                            <Chip
                              label={alarm?.Content?.buttonType || alarm?.deviceModel?.config?.buttonType || 'Unknown'}
                              size="small"
                              color={alarm?.Content?.buttonType ? 'primary' : 'default'}
                            />
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Device Location ID</TableCell>
                          <TableCell>{alarm?.deviceModel?.deviceLocation || 'Not set'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Device Person ID</TableCell>
                          <TableCell>{alarm?.deviceModel?.devicePerson || 'Not set'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Last Seen</TableCell>
                          <TableCell>
                            {alarm?.deviceModel?.last_seen
                              ? new Date(alarm.deviceModel.last_seen).toLocaleString()
                              : 'N/A'}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Device Config</Typography>
                  <Paper variant="outlined" sx={{ p: 2, backgroundColor: 'grey.50', overflow: 'auto', maxHeight: 300 }}>
                    <pre style={{ margin: 0, fontSize: '0.75rem' }}>
                      {JSON.stringify(alarm?.deviceModel?.config || alarm?.Content?.deviceConfig || {}, null, 2)}
                    </pre>
                  </Paper>
                </Grid>
              </Grid>
            ) : (
              <Alert severity="warning">
                Device information not available. The button device was not found in the database when this alarm was created.
                <br /><br />
                <strong>Device HEX Code:</strong> <code>{alarm?.Content?.deviceKey || 'Unknown'}</code>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Antenna Data Tab */}
      {subTab === 2 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AntennaIcon color="primary" />
              Antenna Signal Data
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {alarm?.Content?.antennas && alarm.Content.antennas.length > 0 ? (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>Antenna Name</TableCell>
                      <TableCell>Location</TableCell>
                      <TableCell align="center">RSSI (dBm)</TableCell>
                      <TableCell align="center">Signal Quality</TableCell>
                      <TableCell>Device UUID</TableCell>
                      <TableCell>Hub</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {alarm.Content.antennas
                      .sort((a, b) => (b.rssiDbm || -999) - (a.rssiDbm || -999))
                      .map((antenna, index) => (
                        <TableRow
                          key={index}
                          sx={{ backgroundColor: index === 0 ? 'success.light' : 'inherit' }}
                        >
                          <TableCell>
                            {index + 1}
                            {index === 0 && <Chip label="Best" size="small" color="success" sx={{ ml: 1 }} />}
                          </TableCell>
                          <TableCell>{antenna.antennaName || 'Unknown'}</TableCell>
                          <TableCell>
                            {antenna.locationName ? (
                              <Tooltip title={`Location ID: ${antenna.locationId || 'N/A'}`}>
                                <span style={{ cursor: 'help' }}>{antenna.locationName}</span>
                              </Tooltip>
                            ) : antenna.locationId ? (
                              <Typography variant="body2" color="text.secondary">
                                ID: {antenna.locationId}
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="text.secondary">-</Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={antenna.rssiDbm || 'N/A'}
                              size="small"
                              color={antenna.rssiDbm >= -60 ? 'success' :
                                     antenna.rssiDbm >= -75 ? 'warning' : 'error'}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={antenna.signalQuality || 'Unknown'}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <code style={{ fontSize: '0.75rem' }}>
                              {antenna.deviceUUID?.slice(0, 8) || 'N/A'}
                            </code>
                          </TableCell>
                          <TableCell>
                            {antenna.hubHostname && antenna.hubHostname !== 'Unknown'
                              ? antenna.hubHostname
                              : <Typography variant="body2" color="text.secondary">-</Typography>}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography color="text.secondary">
                No antenna data available
              </Typography>
            )}
          </CardContent>
        </Card>
      )}

      {/* Raw Data Tab (was 4, now 3 after Hub Info removal) */}
      {subTab === 3 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <JsonIcon />
                  Alarm Content (Raw Event Data)
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                  <Tooltip title="Copy to clipboard">
                    <IconButton size="small" onClick={() => copyToClipboard(alarm?.Content)}>
                      <CopyIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Paper variant="outlined" sx={{ p: 2, backgroundColor: 'grey.900', overflow: 'auto', maxHeight: 400 }}>
                  <pre style={{ margin: 0, fontSize: '0.75rem', color: '#00ff00' }}>
                    {JSON.stringify(alarm?.Content, null, 2)}
                  </pre>
                </Paper>
              </AccordionDetails>
            </Accordion>
          </Grid>

          <Grid item xs={12}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <JsonIcon />
                  Location Tree (Snapshot)
                  {(!alarm?.locationTree || alarm.locationTree.length === 0) && (
                    <Chip label="Empty" size="small" color="warning" variant="outlined" />
                  )}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                {alarm?.locationTree && alarm.locationTree.length > 0 ? (
                  <Paper variant="outlined" sx={{ p: 2, backgroundColor: 'grey.900', overflow: 'auto', maxHeight: 300 }}>
                    <pre style={{ margin: 0, fontSize: '0.75rem', color: '#00ff00' }}>
                      {JSON.stringify(alarm.locationTree, null, 2)}
                    </pre>
                  </Paper>
                ) : (
                  <Alert severity="info">
                    Location tree data was not captured for this alarm. This may happen when:
                    <ul style={{ margin: '8px 0 0 0', paddingLeft: 20 }}>
                      <li>The alarm was created before location tree snapshots were implemented</li>
                      <li>No location hierarchy exists for the determined location</li>
                    </ul>
                  </Alert>
                )}
              </AccordionDetails>
            </Accordion>
          </Grid>

          <Grid item xs={12}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <JsonIcon />
                  Contacts (Notification Recipients)
                  {(!alarm?.contacts || alarm.contacts.length === 0) && (
                    <Chip label="Empty" size="small" color="warning" variant="outlined" />
                  )}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                {alarm?.contacts && alarm.contacts.length > 0 ? (
                  <Paper variant="outlined" sx={{ p: 2, backgroundColor: 'grey.900', overflow: 'auto', maxHeight: 300 }}>
                    <pre style={{ margin: 0, fontSize: '0.75rem', color: '#00ff00' }}>
                      {JSON.stringify(alarm.contacts, null, 2)}
                    </pre>
                  </Paper>
                ) : (
                  <Alert severity="info">
                    Contact/recipient data was not captured for this alarm. This may happen when:
                    <ul style={{ margin: '8px 0 0 0', paddingLeft: 20 }}>
                      <li>No emergency service integration is configured for this location</li>
                      <li>The alarm was cancelled during countdown (before notifications were sent)</li>
                      <li>Contact snapshots are not enabled for this alarm type</li>
                    </ul>
                  </Alert>
                )}
              </AccordionDetails>
            </Accordion>
          </Grid>

          <Grid item xs={12}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <JsonIcon />
                  Full Alarm Object
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                  <Tooltip title="Copy to clipboard">
                    <IconButton size="small" onClick={() => copyToClipboard(alarm)}>
                      <CopyIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Paper variant="outlined" sx={{ p: 2, backgroundColor: 'grey.900', overflow: 'auto', maxHeight: 500 }}>
                  <pre style={{ margin: 0, fontSize: '0.75rem', color: '#00ff00' }}>
                    {JSON.stringify(alarm, null, 2)}
                  </pre>
                </Paper>
              </AccordionDetails>
            </Accordion>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default DeveloperTab;
